"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
const bluebird_1 = __importDefault(require("bluebird"));
const complexData_1 = __importDefault(require("@powerednow/complexData/complexData"));
const constants_1 = require("@powerednow/shared/constants");
const date_1 = require("@powerednow/shared/modules/utilities/date");
const notificationDefinition_1 = require("@powerednow/shared/constants/notificationDefinition");
const customerEmailTemplateValues_1 = __importDefault(require("@powerednow/shared/constants/customerEmailTemplateValues"));
const converter_1 = __importDefault(require("@powerednow/shared/modules/utilities/converter"));
const modelProperties_1 = __importDefault(require("./modelProperties"));
const entity_1 = __importDefault(require("./entity"));
const modelLookup_1 = require("../modelLookup");
// eslint-disable-next-line no-redeclare
class Action extends complexData_1.default {
    static async addEta(args) {
        const [_messageType, linkedItem, existingTemplateData] = args;
        if (!existingTemplateData.currentPosition?.lat) {
            return {};
        }
        const company = await linkedItem.getCompany();
        const isEtaTurnedOn = await company.getSettingValue(constants_1.SETTINGS_KEYS.ADD_ETA_TO_ATTENDANCE_NOTIFICATION);
        if (!isEtaTurnedOn) {
            return {};
        }
        const duration = await this.callExternalFunction(linkedItem, 'getETA', [{
                lat: existingTemplateData.currentPosition?.lat,
                long: existingTemplateData.currentPosition?.long,
            }, await linkedItem.getAddress()]);
        if (duration) {
            const now = (0, date_1.getNow)();
            const eta = (0, date_1.addSeconds)(now, duration);
            return {
                eta,
            };
        }
        return {};
    }
    static get allowedAssociations() {
        return {
            company: {
                instance: (0, modelLookup_1.complexFindModel)('Company'),
                key: 'company',
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().company_id,
                },
            },
            job: {
                key: 'job',
                instance: (0, modelLookup_1.complexFindModel)('Job'),
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().job_id,
                },
            },
            site: {
                key: 'contactToAddress',
                instance: (0, modelLookup_1.complexFindModel)('ContactToAddress'),
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().site_id,
                },
            },
            userAction: {
                key: 'userAction',
                instance: (0, modelLookup_1.complexFindModel)('UserAction'),
                cascadeDelete: true,
                condition: {
                    action_id: this.Entity.getFieldSymbols().id,
                },
            },
            message: {
                key: 'message',
                instance: (0, modelLookup_1.complexFindModel)('Message'),
                cascadeDelete: true,
                condition: {
                    action_id: this.Entity.getFieldSymbols().id,
                },
            },
        };
    }
    async getCustomer() {
        const job = await this.getJob();
        if (!job) {
            return null;
        }
        return job.getCustomer();
    }
    async getAddress() {
        const { site, plot, } = await this.getSiteAndPlot();
        const customer = await this.getCustomer();
        return customer?.getAddressForDiary(site, plot) || '';
    }
    async getSiteAndPlot() {
        const jobRecord = await this.getJob();
        if (!jobRecord) {
            return {
                site: null,
                plot: this.data.plot,
            };
        }
        const siteId = this.data.site_id ? this.data.site_id : jobRecord.data.site_id;
        const customer = await this.getCustomer();
        let site = siteId ? await customer.getSiteById(siteId) : null;
        if (site?.data.customer_id !== jobRecord.data.customer_id) {
            site = null;
        }
        return {
            site,
            plot: this.data.plot,
        };
    }
    async findContactableSite(contactChannels) {
        if (this.data.site_id) {
            const site = await this.getSite();
            const isContactable = await site.isContactable(contactChannels);
            if (isContactable) {
                return site;
            }
        }
        const job = await this.getJob();
        if (job) {
            const jobSite = job ? await job.findSite() : null;
            if (jobSite && (await jobSite.isContactable(contactChannels))) {
                return jobSite;
            }
            const customer = await job.getCustomer();
            const mainSite = await customer.getMainContactToAddress();
            if (mainSite && (await mainSite.isContactable(contactChannels))) {
                return mainSite;
            }
        }
        return null;
    }
    async getNotificationContact(channelFilter = []) {
        const company = await this.getCompany();
        const settingValue = await company.getSettingValue(constants_1.SETTINGS_KEYS.CUSTOMER_NOTIFICATION_CONTACT);
        const setting = converter_1.default.toNumberIfPossible(settingValue);
        const sites = [];
        if (setting === notificationDefinition_1.CustomerNotificationGroups.SiteContact.id || setting === notificationDefinition_1.CustomerNotificationGroups.Both.id) {
            const channels = channelFilter.length === 0 ? await company.getNotificationChannels() : channelFilter;
            const contactableSite = await this.findContactableSite(channels);
            if (contactableSite) {
                sites.push(await contactableSite.getContact());
            }
        }
        //
        // If setting is main contact (so we don't go into the above block) or we go into that but
        // didn't find anything or we just need both then get main contact as well
        //
        if (sites.length === 0 || setting === notificationDefinition_1.CustomerNotificationGroups.Both.id) {
            const customer = await this.getCustomer();
            if (customer) {
                sites.push(await customer.getMainContact());
            }
        }
        const filterSites = sites.filter(Boolean);
        // Remove duplicate contacts
        const uniqueSites = filterSites.filter((value, index, self) => self.findIndex(contact => contact.data.id === value.data.id) === index);
        return uniqueSites;
    }
    async findJobSite() {
        return (await this.getJob()).findSite();
    }
    async findSite() {
        const siteId = this.data.site_id;
        if (siteId) {
            return this.getSite();
        }
        return (await this.getJob()).findSite();
    }
    async getAssignedUsers() {
        return bluebird_1.default.reduce(this.getAllUserAction(), async (users, userAction) => {
            const complexUser = await userAction.getUser();
            if (complexUser) {
                users.push(complexUser);
            }
            return users;
        }, []);
    }
    getPlanTypeObject() {
        return Object.values(constants_1.ACTION.PLAN_TYPES).filter(planType => planType.ID === this.data.plan_type)[0];
    }
    async getDefaultSite() {
        const customer = await this.getCustomer();
        return customer.getMainSite();
    }
    async assignedUserNames() {
        const assignedUsers = await this.getAssignedUsers();
        return assignedUsers.map(user => user.getFullName());
    }
    async getPermanentTemplateData() {
        return {};
    }
    async getMessageTemplateData(messageType) {
        //
        // In case of deleted actions the plan type might not be available anymore
        //
        const dateTimeFormat = this.getPlanTypeObject().FORMAT_OPTIONS || constants_1.ACTION.PLAN_TYPES.DATE.FORMAT_OPTIONS;
        const company = await this.getCompany();
        const job = await this.getJob();
        const projectName = job?.data.description;
        const customer = await this.getCustomer();
        const customerName = await customer?.getFullName();
        const jobDescription = await company.getSettingValue(constants_1.SETTINGS_KEYS.SERVICE_PERSON);
        const formattedDurationType = (constants_1.ACTION.APPOINTMENT_DURATION_TEXT[this.data.duration_id] ? `${constants_1.ACTION.APPOINTMENT_DURATION_TEXT[this.data.duration_id]} of ` : '');
        return {
            ...this.data.getPureDataValues(),
            jobDescription,
            durationType: formattedDurationType,
            dateTimeFormat,
            projectName,
            customerName,
            fullname: (await this.assignedUserNames()).join(', '),
            companyname: company.data.name,
            address: await this.getAddress(),
        };
    }
    static calculateDurationForBooking({ day, dayPart, workday_start, workday_end, lunchTime, workdayHalfTime, eveningHours, }) {
        const hours = {
            [constants_1.ACTION.APPOINTMENT_DURATION.WORKING_DAYS]: {
                start: workday_start || constants_1.ACTION.DEFAULT_TIME.WORKDAY_START,
                end: workday_end || constants_1.ACTION.DEFAULT_TIME.WORKDAY_END,
                sameDay: true,
            },
            [constants_1.ACTION.APPOINTMENT_DURATION.MORNING]: {
                start: workday_start || constants_1.ACTION.DEFAULT_TIME.WORKDAY_START,
                end: lunchTime.enabled ? lunchTime.start : workdayHalfTime,
                sameDay: true,
            },
            [constants_1.ACTION.APPOINTMENT_DURATION.AFTERNOON]: {
                start: lunchTime.enabled ? lunchTime.end : workdayHalfTime,
                end: workday_end || constants_1.ACTION.DEFAULT_TIME.WORKDAY_END,
                sameDay: true,
            },
            [constants_1.ACTION.APPOINTMENT_DURATION.EVENING]: {
                start: eveningHours.enabled ? eveningHours.start : (0, date_1.addPlusHours)(workday_end, 1),
                end: eveningHours.enabled ? eveningHours.end : (0, date_1.addPlusHours)(workday_end, 4),
                sameDay: true,
            },
        };
        const start = new Date(day);
        const end = new Date(day);
        const dayPartId = dayPart === 'allDay' ? constants_1.ACTION.APPOINTMENT_DURATION.WORKING_DAYS : constants_1.ACTION.APPOINTMENT_DURATION[(0, date_1.getPartOfDayNameByKey)(dayPart).toUpperCase()];
        const [startHour, startMinute] = hours[dayPartId].start.split(':');
        const [endHour, endMinute] = hours[dayPartId].end.split(':');
        start.setHours(startHour);
        start.setMinutes(startMinute);
        end.setHours(endHour);
        end.setMinutes(endMinute);
        return { start, end, dayPartId };
    }
}
_a = Action;
Action.Entity = entity_1.default;
Action.modelProperties = modelProperties_1.default;
Action.MessageTypeSpecificDataCollectors = {};
Action.MessageTypeSpecificPermanentDataCollectors = {
    [customerEmailTemplateValues_1.default.ATTENDING_APPOINTMENT.ID]: [_a.addEta],
};
exports.default = Action;
//# sourceMappingURL=../../../../src/models/action/index.js.map